import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, untracked, viewChild } from '@angular/core';
import { PermissionService } from '@core/services/permission.service';
import { CounsellingIntakeAssessmentFormComponent } from '@features/intervention/assessment/cia/components/cia-form/cia-form.component';
import { CiaPart } from '@features/intervention/assessment/cia/cia-form-groups';
import { SaveCounsellingIntakeAssessmentRequest } from '@features/intervention/assessment/cia/cia.model';
import { CiaLookupStore } from '@features/intervention/assessment/cia/cia-lookup.store';
import { Permission } from '@shared/enums/permission.enum';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionStore } from '../../../../intervention.store';

// What saving an assessment part comes to: saved (or nothing to save), a part of the form that needs attention first, or a server refusal.
export type CiaSaveOutcome = 'saved' | 'failed' | CiaPart;

// The clinical assessment (CIA) inside the case's wizard. The case shows one part of the assessment at a time (Presenting problem, Clinical
// assessment, Functional impact and risk, Treatment planning) and this component keeps one form for all of them, so what has been entered
// stays while the user moves between the parts. It creates the assessment the first time something is saved, saves a draft when asked, and locks
// or reopens it. The case moves on to Referral when the assessment is locked, so the case is told whenever something was saved.
@Component({
  selector: 'cia-host',
  imports: [DatePipe, CounsellingIntakeAssessmentFormComponent, ...SharedMaterialUIModule],
  templateUrl: './cia-host.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CiaHostComponent {
  private readonly permissions = inject(PermissionService);
  // The case's own state (its assessment and its risk screeners); the reference lists of the form are separate.
  protected readonly store = inject(InterventionStore);
  protected readonly lookupStore = inject(CiaLookupStore);

  readonly interventionId = input.required<string>();
  // Whether the case has an assessment (it is started with the first save).
  readonly hasCia = input(false);
  readonly part = input.required<CiaPart>();
  readonly interventionStatus = input<string | null>(null);
  // The finer status of the case (Assessment Complete once the assessment is ready to be locked).
  readonly subStatus = input<string | null>(null);
  // Something was saved, locked or reopened, so the case reads its status and progress again.
  readonly changed = output<void>();

  private readonly form = viewChild(CounsellingIntakeAssessmentFormComponent);
  // Called when the save that was asked for is done.
  private pendingSave: ((outcome: CiaSaveOutcome) => void) | null = null;

  // The assessment of this case (the store forgets it when another case is opened).
  protected readonly cia = this.store.cia;

  // The latest Suicide Risk Screener of the case.
  protected readonly latestScreener = computed(() => {
    const [latest] = [...this.store.riskScreeners()].sort(
      (a, b) => b.screenedDate.localeCompare(a.screenedDate) || b.recordedDate.localeCompare(a.recordedDate),
    );
    return latest ? { screenedDate: latest.screenedDate, riskRatingName: latest.riskRatingName } : null;
  });

  // The first save creates the assessment and later saves update it, so one permission allows both.
  protected readonly canUpdate = computed(() => this.permissions.hasPermission(Permission.CounsellingIntakeAssessmentUpdate));

  // Locked, closed, or not allowed to change: the parts are shown but cannot be edited.
  protected readonly readOnly = computed(() => !this.canUpdate() || !!this.cia()?.isFinalized || this.interventionStatus() === 'Closed');

  constructor() {
    this.lookupStore.loadLookups();

    // Opens the case's assessment, when it has one; a case that has none starts with an empty form.
    effect(() => {
      const interventionId = this.interventionId();
      const hasCia = this.hasCia();
      untracked(() => {
        if (hasCia && !this.store.cia() && !this.store.ciaLoading()) {
          this.store.loadCia(interventionId);
        }
      });
    });

    effect(() => {
      const interventionId = this.interventionId();
      untracked(() => {
        if (this.permissions.hasPermission(Permission.RiskScreenerRead)) {
          this.store.loadRiskScreeners(interventionId);
        }
      });
    });
  }

  // Saves what has been entered, when something has. Resolves with what came of it, so the case can stay where it is when it did not work.
  saveIfChanged(): Promise<CiaSaveOutcome> {
    const form = this.form();
    return !form || !form.hasChanges() || this.readOnly() ? Promise.resolve('saved') : this.save(form);
  }

  // Shows what is missing in the part that is open.
  showFirstInvalidField(): void {
    this.form()?.showFirstInvalidField();
  }

  protected saveDraft(): void {
    void this.saveIfChanged();
  }

  // Locks the assessment (after saving what has been entered); the case moves on to Referral.
  protected async lock(): Promise<void> {
    const outcome = await this.saveIfChanged();

    if (outcome === 'saved' && this.cia()) {
      this.store.finalizeCia(this.interventionId(), () => this.changed.emit());
    }
  }

  protected reopen(): void {
    if (this.cia()) {
      this.store.reopenCia(this.interventionId(), () => this.changed.emit());
    }
  }

  private save(form: CounsellingIntakeAssessmentFormComponent): Promise<CiaSaveOutcome> {
    return new Promise((resolve) => {
      this.pendingSave = resolve;
      const part = form.requestSave();

      if (part) {
        this.pendingSave = null;
        resolve(part);
      }
    });
  }

  protected onSubmitted(request: SaveCounsellingIntakeAssessmentRequest): void {
    const finish = (outcome: CiaSaveOutcome) => {
      this.pendingSave?.(outcome);
      this.pendingSave = null;
      if (outcome === 'saved') {
        this.changed.emit();
      }
    };

    this.store.saveCia(this.interventionId(), request, () => finish('saved'), () => finish('failed'));
  }
}
